import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { userService } from '@/services/user.service'
import { accountService } from '@/services/account.service'
import type { UserCreateDto, UserUpdateDto } from '@/types/user.types'

export const useUsers = (activeOnly?: boolean) => {
  return useQuery({
    queryKey: ['users', activeOnly],
    queryFn: () => userService.getUsers(activeOnly),
    staleTime: 60000
  })
}

export const useUser = (userId: string) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => userService.getUser(userId),
    enabled: !!userId
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (user: UserCreateDto) => userService.createUser(user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    }
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, user }: { userId: string; user: UserUpdateDto }) =>
      userService.updateUser(userId, user),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', variables.userId] })
    }
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    }
  })
}

export const useUserLinkedAccounts = (userId: string) => {
  return useQuery({
    queryKey: ['user-linked-accounts', userId],
    queryFn: () => userService.getUserLinkedAccounts(userId),
    enabled: !!userId
  })
}

export const useSetDefaultAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId, userId }: { accountId: string; userId: string }) =>
      accountService.linkUser(accountId, userId, { isDefault: true }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-linked-accounts', variables.userId] })
    }
  })
}

export const useToggleUserAdmin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId, userId, isAdmin }: { accountId: string; userId: string; isAdmin: boolean }) =>
      accountService.toggleUserAdmin(accountId, userId, isAdmin),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-linked-accounts', variables.userId] })
    }
  })
}
